import { useContext, useEffect, useState } from 'react';
import TreeSection from './components/TreeSection';
import BackButton from '@/comp/backButton/backButton';
import Button, { ButtonProps } from '@/comp/button/button';
import { DisplayedQuestions, OnSpotAnswer, OnSpotContextProps, OnSpotReturnTree } from '@/model/onSpot/OnSpot';
import OnSpotApi from '@/api/onSpot/OnSpot';
import { useNavigate } from 'react-router';
import { OnSpotContext } from '../index';
import { PageState } from '@/model/common/PageState';
import FormSkeleton from '@/comp/skeleton/form/FormSkeleton';

interface OnSpotFormProps {
    setDisplayedQuestions: (displayedQuestions: DisplayedQuestions) => void;
    saveEntrypoint: (entrypoint: Number[]) => void;
}

export const OnSpotForm = ({ setDisplayedQuestions, saveEntrypoint }: OnSpotFormProps) => {
    const {
        routes,
        dataTree,
        setDataTree,
        treeSectionId,
        savedEntrypoint,
        setTreeSectionId,
        dataReturnTree,
        setDataReturnTree,
        setTreeSectionMapping,
        setFileLists,
    } = useContext(OnSpotContext) as OnSpotContextProps;
    const [entrypoint, setEntrypoint] = useState<Number[]>(savedEntrypoint);
    const [formValid, setFormValid] = useState(false);
    const [pageState, setPageState] = useState<PageState>(PageState.Loading);
    const [showError, setShowError] = useState<boolean>(false);
    const navigate = useNavigate();

    const submitButtonProps = {
        type: 'submit',
        label: 'Weiter zu den Nachweisen',
        validated: false,
    } satisfies ButtonProps;

    useEffect(() => {
        const validateForm = () => {
            const validateRecursive = (refId: string) => {
                setShowError(false);
                let valid = true;
                if (dataReturnTree[refId]) {
                    if (dataReturnTree[refId].answers.length > 0) {
                        dataReturnTree[refId].answers.forEach((answer) => {
                            const dataTreeAnswerIndex = dataTree[refId].answers.findIndex((a) => a.id === answer.id);
                            const dataTreeAnswer = dataTree[refId].answers[dataTreeAnswerIndex];

                            if (dataTreeAnswer.type === 'text' && answer.freeText === '' && dataTreeAnswer.required) {
                                valid = false;
                                return false;
                            } else if (answer.$refId) {
                                valid = valid && validateRecursive(answer.$refId);
                            }
                        });
                    }
                } else {
                    valid = false;
                }
                return valid;
            };
            entrypoint?.forEach((value) => {
                const newDataTreeId = Object.keys(dataTree).filter((key) => dataTree[key].dbId === value);
                if (newDataTreeId.length > 0) {
                    const valid = validateRecursive(`question-${dataTree[newDataTreeId[0]].id}`);
                    if (valid !== formValid) {
                        setFormValid(valid);
                    }
                }
            });
        };

        if (dataReturnTree) {
            validateForm();
        }
    }, [dataReturnTree, formValid, dataTree, entrypoint]);

    useEffect(() => {
        saveEntrypoint(entrypoint);
    }, [entrypoint, saveEntrypoint]);

    const handleSubmit = () => {
        setDataReturnTree(dataReturnTree);
        setFileLists({});
        if (formValid) {
            navigate(`/${routes?.root}/${routes?.children?.upload}`);
        } else {
            setShowError(true);
        }
    };

    const loadAutomaticNextDataTree = async (answer: OnSpotAnswer) => {
        const params = new URLSearchParams({ getSubsequentTree: 'true', id: answer.dbId.toString() });

        return OnSpotApi.getOnSpotTree(params).then((data) => {
            if (data) {
                const displayedQuestions: DisplayedQuestions = {};
                setDisplayedQuestions({ ...displayedQuestions });
                return data;
            } else {
                setPageState(PageState.Error);
            }
        });
    };

    if (dataTree && entrypoint.length < 1) {
        const newReturnTree: OnSpotReturnTree = { ...dataReturnTree };
        if (dataTree['entrypoint']?.answers.length === 1) {
            setPageState(PageState.Loading);
            setEntrypoint([dataTree['entrypoint']?.answers[0]?.dbId]);
            const firstAnswer = dataTree['entrypoint']?.answers[0];
            newReturnTree['entrypoint'] = {
                questionId: dataTree['entrypoint'].id,
                dbId: dataTree['entrypoint'].dbId,
                answers: [{ id: firstAnswer.id, dbId: firstAnswer?.dbId, $refId: 'question-entrypoint' }],
            };
            loadAutomaticNextDataTree(dataTree['entrypoint']?.answers[0]).then((response) => {
                setTreeSectionMapping({ entrypoint: dataTree['entrypoint']?.answers[0]?.label });
                setPageState(PageState.Idle);
                if (response) {
                    setTreeSectionId(response['entrypoint']?.id.toString());
                    setDataTree(response);
                }
                setDataReturnTree(newReturnTree);
            });
        }
    }

    if (dataTree && dataTree['entrypoint']?.answers.length > 1 && pageState !== PageState.Idle) {
        setPageState(PageState.Idle);
    }

    if (pageState === PageState.Loading) {
        return <FormSkeleton />;
    } else {
        return (
            <>
                <div className="content-wrapper">
                    <div className="intro">
                        <h1 className="color-primary">Hilfe vor Ort</h1>
                        <div className="intro-text">
                            <p>
                                Sollten Sie wider Erwarten während Ihrer Reise nicht zufrieden sein, können Sie Ihr
                                Anliegen hier direkt an AurumTours übermitteln. So helfen wir Ihnen nicht nur schneller,
                                sondern Sie erfüllen gleichzeitig Ihre Verantwortlichkeit zur Mängelanzeige – für einen
                                unbeschwerten Urlaub.
                            </p>
                        </div>
                    </div>
                </div>
                {pageState === PageState.Idle &&
                    dataTree &&
                    dataTree['entrypoint']?.answers.length > 1 &&
                    treeSectionId && (
                        <>
                            {dataTree['entrypoint'] && (
                                <section key="entry">
                                    <TreeSection
                                        saveDisplayedQuestions={setDisplayedQuestions}
                                        key={`tree-${dataTree['entrypoint'].id}`}
                                    />
                                </section>
                            )}
                            <section className="section-submit">
                                <div className="container justify-between">
                                    <BackButton class="col" target="blank" />
                                    <Button
                                        type={submitButtonProps.type}
                                        label={submitButtonProps.label}
                                        validated={submitButtonProps.validated}
                                        valid={formValid}
                                        handleClick={handleSubmit}
                                    />
                                </div>
                                <div className="container justify-center">
                                    {showError && !formValid && (
                                        <p className="error text-red">
                                            Es scheint so, als hätten Sie das Formular noch nicht komplett ausgefüllt.
                                            Bitte beantworten Sie die restlichen Fragen und versuchen es erneut.
                                        </p>
                                    )}
                                </div>
                            </section>
                        </>
                    )}
            </>
        );
    }
};
